/* eslint-disable @typescript-eslint/no-explicit-any */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from "react";
import cx from "classnames";
import { Form, Field } from "react-final-form";
import { Wrapper } from "@cinch-nx/shared-ui";
import { Primary, Text, TextFontSizes, TextFontWeights, TextLineHeights } from "@cinch-labs/design-system";
import styles from "./find-a-car.module.css";
import ladyWithCarKeysLarge from "./assets/lady-with-car-keys-large.png";
import ladyWithCarKeysSmall from "./assets/lady-with-car-keys-small.png";
import { currencyValueFormatter, interpolateI18n } from "@cinch-labs/shared-util";
import { convertToPence, formatToPounds, getActiveCtaText, getFiltredUrl } from "./find-a-car.helpers";
import FindACarRadioField from "./find-a-car-radio-field/find-a-car-radio-field";
import FindACarTextField from "./find-a-car-text-field/find-a-car-text-field";
import { useAnalytics } from "@cinch-nx/shared-analytics";
import { financeBudgetCalculator } from "../../tracking/tracking-events";
var FinanceType;
(function(FinanceType) {
    FinanceType["PCP"] = "PCP";
    FinanceType["HP"] = "HP";
})(FinanceType || (FinanceType = {}));
const calculateHpCarPrice = (monthlyBudget)=>{
    const years = 4;
    const flatInterestRate = 5.14;
    const x = monthlyBudget / (1 + years * flatInterestRate / 100);
    return x * (years * 12) + 2000;
};
function FindACar({ i18n  }) {
    const baseCtaUrl = "/used-cars";
    const [formData, setFormData] = useState({
        budgetType: FinanceType.PCP
    });
    const [ctaText, setCtaText] = useState({
        aria: "",
        short: "",
        long: ""
    });
    const [ctaUrl, setCtaUrl] = useState(baseCtaUrl);
    const [budgetDescription, setBudgetDescription] = useState([]);
    const { trackEvent  } = useAnalytics();
    const budgetTypeOptionsLabel = (index, checked)=>/*#__PURE__*/ _jsx(Text, {
            size: "s",
            fontWeight: checked ? "bold" : "medium",
            children: i18n.searchSection[index].label
        });
    const budgetTypeOptionsSubLabel = (index, checked)=>/*#__PURE__*/ _jsx(Text, {
            size: "s",
            className: cx(styles.budgetTypeText, {
                [styles.budgetTypeHidden]: !checked
            }),
            children: i18n.searchSection[index].sublabel
        });
    const budgetTypeOptions = Object.entries(FinanceType).map(([key, value])=>{
        const index = key;
        return {
            value: value,
            label: (checked)=>budgetTypeOptionsLabel(index, checked),
            subLabel: (checked)=>budgetTypeOptionsSubLabel(index, checked)
        };
    });
    const onSubmit = (values)=>{
        if (!values.budgetType) {
            return;
        }
        trackFormEvents(values);
    };
    const trackFormEvents = (values)=>{
        const trackValue = (()=>{
            const budget = Number.parseInt(values.budget || "", 10);
            if (values.budget === "" || values.budget === undefined) {
                return "-";
            }
            if (!Number.isNaN(budget)) {
                return `${budget / 100}`;
            }
            return "";
        })();
        trackEvent(financeBudgetCalculator(values.budgetType, trackValue));
    };
    useEffect(()=>{
        const budget = Number.parseInt(formData.budget || "", 10);
        if (formData.budget && !Number.isNaN(budget)) {
            const budgetInPounds = budget / 100;
            const isPCP = formData.budgetType === FinanceType.PCP;
            const hpCarPrice = Math.round(calculateHpCarPrice(budgetInPounds));
            const hpCarPriceFormatted = currencyValueFormatter(hpCarPrice);
            const hpBudgetDescription = interpolateI18n(i18n.inputSection.hpBudgetDescription, {
                calculateHpCarPrice: hpCarPriceFormatted,
                budget: currencyValueFormatter(budgetInPounds)
            }, {
                b: (v)=>/*#__PURE__*/ _jsx(Text, {
                        element: "span",
                        fontWeight: "bold",
                        children: v
                    }, v)
            });
            const message = getActiveCtaText(i18n.cta.active.message, isPCP ? budgetInPounds : hpCarPrice);
            if (!isPCP) {
                setBudgetDescription(hpBudgetDescription);
                setCtaUrl(getFiltredUrl(baseCtaUrl, hpCarPrice, false));
            } else {
                setCtaUrl(getFiltredUrl(baseCtaUrl, budgetInPounds, true));
            }
            setCtaText({
                aria: `${message}${isPCP ? i18n.cta.active.suffix.aria : ""}`,
                short: `${message}${isPCP ? i18n.cta.active.suffix.short : ""}`,
                long: `${message}${isPCP ? i18n.cta.active.suffix.long : ""}`
            });
        } else {
            setCtaText({
                aria: i18n.cta.default,
                short: i18n.cta.default,
                long: i18n.cta.default
            });
            setCtaUrl(baseCtaUrl);
            setBudgetDescription([]);
        }
    }, [
        formData.budget,
        formData.budgetType
    ]);
    return /*#__PURE__*/ _jsx(Wrapper, {
        children: /*#__PURE__*/ _jsxs("article", {
            className: styles.findACar,
            children: [
                /*#__PURE__*/ _jsx(Text, {
                    element: "h2",
                    className: styles.financeFeatureTitle,
                    fontSize: TextFontSizes["4xl"],
                    fontWeight: TextFontWeights.bold,
                    lineHeight: TextLineHeights.display,
                    children: i18n.title
                }),
                /*#__PURE__*/ _jsxs("picture", {
                    className: styles.financeFeaturePicture,
                    children: [
                        /*#__PURE__*/ _jsx("source", {
                            srcSet: `${ladyWithCarKeysSmall.src} 1x, ${ladyWithCarKeysSmall.src} 2x`,
                            type: "image/png",
                            media: "(max-width: 767px)"
                        }),
                        /*#__PURE__*/ _jsx("img", {
                            className: styles.financeFeatureImage,
                            srcSet: `${ladyWithCarKeysLarge.src} 1x, ${ladyWithCarKeysLarge.src} 2x`,
                            alt: i18n.alt,
                            loading: "lazy"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Form, {
                    initialValues: formData,
                    onSubmit: onSubmit,
                    render: ({ handleSubmit , values  })=>/*#__PURE__*/ _jsxs("form", {
                            className: styles.financeFeatureForm,
                            onSubmit: handleSubmit,
                            children: [
                                /*#__PURE__*/ _jsx(Field, {
                                    name: "budgetType",
                                    component: FindACarRadioField,
                                    options: budgetTypeOptions,
                                    label: i18n.searchSection.subtitle,
                                    classNames: {
                                        container: styles.budgetTypeOptionsContainer,
                                        legend: styles.budgetTypeOptionLegend,
                                        radio: {
                                            container: styles.budgetTypeOptionRadioContainer,
                                            label: styles.budgetTypeOptionRadioLabel
                                        }
                                    },
                                    onChange: (evt)=>{
                                        setFormData({
                                            ...formData,
                                            budgetType: evt.target.value
                                        });
                                    }
                                }),
                                /*#__PURE__*/ _jsx(Field, {
                                    id: "budgetInput",
                                    name: "budget",
                                    component: FindACarTextField,
                                    label: i18n.inputSection.subtitle,
                                    placeholder: `£`,
                                    format: formatToPounds,
                                    inputMode: "numeric",
                                    fullWidth: true,
                                    classNames: {
                                        container: styles.searchInputContainer,
                                        label: {
                                            label: styles.searchInputLabel
                                        }
                                    },
                                    maxLength: 5,
                                    onChange: (evt)=>{
                                        setFormData({
                                            ...formData,
                                            budget: convertToPence(evt.target.value)
                                        });
                                    }
                                }),
                                budgetDescription.length > 0 && formData.budgetType === FinanceType.HP && /*#__PURE__*/ _jsx(Text, {
                                    fontWeight: "book",
                                    fontSize: "m",
                                    className: styles.budgetDescription,
                                    children: budgetDescription
                                }),
                                /*#__PURE__*/ _jsx(Text, {
                                    className: styles.searchDescription,
                                    fontWeight: "book",
                                    fontSize: "xs",
                                    children: i18n.inputSection.description
                                }),
                                /*#__PURE__*/ _jsxs(Primary, {
                                    isFullWidth: true,
                                    element: "a",
                                    href: ctaUrl,
                                    "aria-label": ctaText.aria,
                                    onClick: ()=>trackFormEvents(values),
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            "data-testid": "ctaTextShort",
                                            className: styles.searchCtaShort,
                                            children: `${ctaText.short}`
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            "data-testid": "ctaTextLong",
                                            className: styles.searchCtaLong,
                                            children: `${ctaText.long}`
                                        })
                                    ]
                                })
                            ]
                        })
                })
            ]
        })
    });
}
export default FindACar;
